import {FC, useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {usePrevious} from '../../../../../../srcCommon/helpers/hookHelpers';
import {useIsFocused} from '../../../../../helpers/hooks/focusHookHelpers';
import {
  findLastActivityDate,
  findUnreadStatus,
  UnreadStatusesEnum,
} from '../../../../../helpers/models/conversationHelpers';
import {ResolveDispatchProps} from '../../../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../helpers/reselectHelpers';
import {MarkAsReadDelaysEnum} from '../../../../../models/api/preferencesModel';
import {AppState} from '../../../../../store/appState';
import {toggleMarkConversationAsSeenAction} from '../../../../../store/data/conversations/seenConversationsActions';
import {getConversations, getIsImpersonating} from '../../../../../store/data/dataSelectors';
import {getMarkAsReadDelay} from '../../../../../store/data/preferences/preferencesSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';

/*
 * Props.
 */

interface ConversationTimelineSeenMarkerProps {
  conversationId: number;
  cellsContext: CellsContext;
}

function getConversationId(_state: AppState, props: ConversationTimelineSeenMarkerProps) {
  return props.conversationId;
}

function getCellsContext(_state: AppState, props: ConversationTimelineSeenMarkerProps) {
  return props.cellsContext;
}

function makeGetConversation() {
  return createSelector(
    [getConversationId, getConversations],
    (conversationId, conversations) => conversations[conversationId],
  );
}

function makeGetUnreadStatus(getConversation: ReturnType<typeof makeGetConversation>) {
  return createSelector([getConversation, getCellsContext], (conversation, cellsContext) => {
    if (!conversation) {
      return undefined;
    }

    return findUnreadStatus(cellsContext, conversation);
  });
}

function makeGetLastActivityDate(getConversation: ReturnType<typeof makeGetConversation>) {
  return createSelector([getConversation, getCellsContext], (conversation, cellsContext) => {
    if (!conversation) {
      return undefined;
    }

    return findLastActivityDate(conversation, cellsContext.teammateId);
  });
}

/*
 * Component.
 */

type ConversationTimelineSeenMarkerCombinedProps = ConversationTimelineSeenMarkerProps &
  ResolveDispatchProps<typeof mapDispatchToProps> &
  ResolveStateProps<typeof mapStateToProps>;

// TODO: De-dupe with MobileViewerTimelineMarkAsReadContainer.
const ConversationTimelineSeenMarker: FC<ConversationTimelineSeenMarkerCombinedProps> = (props) => {
  // Whether or not we need to bother marking as read.
  const shouldAutoMarkAsRead = !props.isImpersonating && props.markAsReadDelay !== MarkAsReadDelaysEnum.NEVER;

  // Will re-render and run effects when the focus state of the window changes.
  const isWindowFocused = useIsFocused();

  // To help detect state transitions in conversation read status.
  const isConversationRead = props.unreadStatus === UnreadStatusesEnum.NONE;
  const wasConversationRead = usePrevious(isConversationRead);
  const didConversationBecomeUnread = wasConversationRead && !isConversationRead;

  // To help detect if the read status changed because of new content or not.
  const {lastActivityDate} = props;
  const previousLastActivityDate = usePrevious(lastActivityDate);
  const didReceiveNewActivity =
    lastActivityDate && previousLastActivityDate && lastActivityDate !== previousLastActivityDate;

  // Track if a transition from read -> unread happened while the window was not focused.
  const didConversationBecomeUnreadInBackgroundRef = useRef(false);

  // Track if a transition from read -> unread was caused by new activity.
  const didConversationBecomeUnreadForNewActivity = didConversationBecomeUnread && didReceiveNewActivity;

  const maybeMarkAsRead = () => {
    // Bail out if the conversation is already read.
    if (isConversationRead) {
      return;
    }

    if (shouldAutoMarkAsRead) {
      props.markAsSeen();
    }
  };

  // Run effect every time.
  useEffect(() => {
    if (!isWindowFocused && didConversationBecomeUnreadForNewActivity) {
      didConversationBecomeUnreadInBackgroundRef.current = true;
    }

    if (isWindowFocused) {
      if (didConversationBecomeUnreadInBackgroundRef.current) {
        didConversationBecomeUnreadInBackgroundRef.current = false;
        maybeMarkAsRead();
      }

      if (didConversationBecomeUnreadForNewActivity) {
        maybeMarkAsRead();
      }
    }
  });

  return null;
};

const mapStateToProps = makeMapStateToProps<ConversationTimelineSeenMarkerProps>()(() => {
  const getConversation = makeGetConversation();
  const getUnreadStatus = makeGetUnreadStatus(getConversation);
  const getLastActivityDate = makeGetLastActivityDate(getConversation);

  return {
    markAsReadDelay: getMarkAsReadDelay,
    isImpersonating: getIsImpersonating,
    unreadStatus: getUnreadStatus,
    lastActivityDate: getLastActivityDate,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ConversationTimelineSeenMarkerProps>()((props) => ({
  markAsSeen: () =>
    toggleMarkConversationAsSeenAction([props.conversationId], true, {
      cellsContext: props.cellsContext,
    }),
}));

export const ConversationTimelineSeenMarkerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConversationTimelineSeenMarker);
